import { useState, useEffect } from "react";
import axios from "axios";
import FormInput from "../../../components/input/FormInput";
import "./KVSStemTrainingForm.css";
import { base_url } from "../../../components/apiCall/webserviceurl";

const POST_ENDPOINT = "/pre-training-form";
const GET_ENDPOINT = "/get-pre-training-form";

/* ---------------------------------------------------------------------
    RatingCard — a single 1–5 Likert question rendered as a row of
    star icons (same component/styling used in the post-training
    feedback form). Click a star to set the rating; hovering previews
    the value before it's committed.
  --------------------------------------------------------------------- */
function RatingCard({
  id,
  label,
  required,
  value,
  onChange,
  error,
  min = 1,
  max = 5,
  minLabel = "Poor",
  maxLabel = "Excellent",
}) {
  const [hoverValue, setHoverValue] = useState(0);

  const values = [];
  for (let v = min; v <= max; v++) values.push(v);

  const numericValue = Number(value) || 0;
  const activeValue = hoverValue || numericValue;

  return (
    <div className={`rc-field${error ? " rc-field--error" : ""}`} id={id}>
      <p className="rc-label">
        {label}
        {required && <span className="rc-required">&nbsp;*</span>}
      </p>

      <div className="rc-stars-row ">
        <div
          className="rc-stars"
          style={{ display: "flex", flexDirection: "row" }}
          role="radiogroup"
          aria-label={label}
          onMouseLeave={() => setHoverValue(0)}
        >
          {values.map((v) => {
            const selected = String(value) === String(v);
            const filled = v <= activeValue;
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${v} star${v === 1 ? "" : "s"}`}
                className={`rc-star-btn${filled ? " rc-star-btn--filled" : ""}`}
                onMouseEnter={() => setHoverValue(v)}
                onFocus={() => setHoverValue(v)}
                onBlur={() => setHoverValue(0)}
                onClick={() => onChange(v)}
              >
                <svg viewBox="0 0 24 24" className="rc-star-icon">
                  <path d="M12 2.6l2.95 6.28 6.75.77-5.02 4.72 1.36 6.85L12 17.9l-6.04 3.32 1.36-6.85-5.02-4.72 6.75-.77L12 2.6z" />
                </svg>
              </button>
            );
          })}
        </div>
        <span
          className={`rc-star-value${numericValue ? " rc-star-value--set" : ""}`}
        >
          {numericValue ? `${numericValue}/${max}` : "Not rated"}
        </span>
      </div>

      <div className="rc-scale-labels">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>

      {error && <p className="rc-error">{error}</p>}
    </div>
  );
}

// Inline styles for the star-rating UI only — kept scoped/self-contained
// so the rest of KVSStemTrainingForm.css doesn't need to change.
function RatingStyles() {
  return (
    <style>{`
.rc-field {
  display: flex;
  flex-direction: column;
}

.rc-label {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.5;
  margin: 0 0 14px;
}

.rc-required {
  color: #e11d48;
}

.rc-stars-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.rc-stars {
  display: flex;
  gap: 4px;
}

.rc-star-btn {
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: transform 0.12s ease, background-color 0.15s ease;
}

.rc-star-btn:hover,
.rc-star-btn:focus-visible {
  transform: scale(1.14);
  background: #f0fdf4;
}

.rc-star-btn:focus-visible {
  outline: 2px solid #16a34a;
  outline-offset: 1px;
}

.rc-star-btn:active {
  transform: scale(0.94);
}

.rc-star-icon {
  width: 30px;
  height: 30px;
  fill: #e5e7eb;
  stroke: #d1d5db;
  stroke-width: 1;
  transition: fill 0.15s ease, stroke 0.15s ease, filter 0.15s ease;
}

.rc-star-btn--filled .rc-star-icon {
  fill: #fbbf24;
  stroke: #f59e0b;
  filter: drop-shadow(0 2px 4px rgba(245, 158, 11, 0.35));
}

.rc-star-value {
  font-size: 12.5px;
  font-weight: 700;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.rc-star-value--set {
  color: #92660a;
  background: #fef3c7;
}

.rc-scale-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  max-width: 210px;
  font-size: 12px;
  color: #9ca3af;
}

.rc-error {
  color: #e11d48;
  font-size: 12.5px;
  margin: 8px 0 0;
}

.rc-field--error .rc-star-icon {
  stroke: #fda4af;
}

.kst-loading-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #d3f0dd;
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 14px;
}

.kst-loaded-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #92660a;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 14px;
}

.kst-submit-error {
  color: #e11d48;
  font-size: 12.5px;
  margin: 10px 0 0;
}

@media (max-width: 620px) {
  .rc-star-icon {
    width: 26px;
    height: 26px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rc-star-btn,
  .rc-star-icon {
    transition: none;
    animation: none;
  }
}
    `}</style>
  );
}

const initialState = {
  name: "",
  schoolName: "",
  kvCode: "",
  trainingStart: "",
  trainingEnd: "",
  qualification: "",
  classesTaught: "",
  teachingExperience: "",
  subjectsTaught: "",
  interestEmergingTech: "",
  interestProgramming: "",
  interestProjects: "",
  integratedBefore: "",
  effectivenessHandsOn: "",
  effectivenessLectures: "",
  knowledgePreSessions: "",
  familiarityConcepts: "",
  techUsageFrequency: "",
  challengesFaced: "",
};

// Screenshot cuts off before revealing the dropdown's actual options —
// swap these for the real experience bands whenever you have them.
const experienceOptions = [
  "Less than 1 year",
  "1 - 3 years",
  "4 - 7 years",
  "8 - 15 years",
  "More than 15 years",
];

// The backend's rating question_code for these two fields carries a
// typo ("effectictiveness..." instead of "effectiveness...") — this
// map translates between the two so the UI keys can stay correctly
// spelled while the API payload matches exactly what the backend
// expects.
const FIELD_TO_API_QUESTION_CODE = {
  effectivenessHandsOn: "effectivenessHandsOn",
  effectivenessLectures: "effectictivenessLectures",
};

const API_QUESTION_CODE_TO_FIELD = Object.fromEntries(
  Object.entries(FIELD_TO_API_QUESTION_CODE).map(([field, code]) => [
    code,
    field,
  ]),
);

function toApiQuestionCode(fieldKey) {
  return FIELD_TO_API_QUESTION_CODE[fieldKey] || fieldKey;
}

function fromApiQuestionCode(questionCode) {
  return API_QUESTION_CODE_TO_FIELD[questionCode] || questionCode;
}

// The 8 rating fields that are part of the /pre-training-form payload,
// in the order used to build the `ratings` array.
const ratingFieldKeys = [
  "interestEmergingTech",
  "interestProgramming",
  "interestProjects",
  "effectivenessHandsOn",
  "effectivenessLectures",
  "knowledgePreSessions",
  "familiarityConcepts",
  "techUsageFrequency",
];

function validate(fields) {
  const errors = {};
  // if (!fields.name.trim()) errors.name = "Name is required.";
  // if (!fields.schoolName.trim()) errors.schoolName = "School Name is required.";
  // if (!fields.trainingStart)
  //   errors.trainingStart = "Training start date is required.";
  // if (!fields.trainingEnd)
  //   errors.trainingEnd = "Training end date is required.";
  if (!fields.qualification)
    errors.qualification = "Please select your highest qualification.";
  if (!fields.classesTaught)
    errors.classesTaught = "Please select the classes/grades you taught.";
  if (!fields.teachingExperience)
    errors.teachingExperience = "Please select your years of experience.";
  if (!fields.subjectsTaught.trim())
    errors.subjectsTaught = "Please enter the subject(s) you teach.";
  if (!fields.interestEmergingTech)
    errors.interestEmergingTech = "Please rate your interest.";
  if (!fields.interestProgramming)
    errors.interestProgramming = "Please rate your interest.";
  if (!fields.interestProjects)
    errors.interestProjects = "Please rate your interest.";
  if (!fields.integratedBefore)
    errors.integratedBefore = "Please select Yes or No.";
  if (!fields.effectivenessHandsOn)
    errors.effectivenessHandsOn = "Please rate the effectiveness.";
  if (!fields.effectivenessLectures)
    errors.effectivenessLectures = "Please rate the effectiveness.";
  if (!fields.knowledgePreSessions)
    errors.knowledgePreSessions = "Please rate your knowledge.";
  if (!fields.familiarityConcepts)
    errors.familiarityConcepts = "Please rate your familiarity.";
  if (!fields.techUsageFrequency)
    errors.techUsageFrequency = "Please rate how often you use technology.";
  if (!fields.challengesFaced.trim())
    errors.challengesFaced = "Please describe the challenges you face.";
  return errors;
}

// Builds the exact payload shape the /pre-training-form API expects.
function buildFeedbackPayload(fields) {
  return {
    form_type: "pre_training",
    classes_taught: fields.classesTaught,
    integrated_before: fields.integratedBefore,
    challenges_faced: fields.challengesFaced,
    subjects_taught: [fields.subjectsTaught],
    teaching_experience: fields.teachingExperience,
    qualification: fields.qualification,
    ratings: ratingFieldKeys.map((key) => ({
      question_code: toApiQuestionCode(key),
      rating: Number(fields[key]),
    })),
  };
}

// Maps the /get-pre-training-form API response shape into the flat
// `fields` state the form uses. Only overwrites keys the response
// actually has data for — every other field (name, schoolName,
// training dates, etc., which this GET response doesn't return) is
// left untouched.
function mapExistingFeedbackToFields(responseData, currentFields) {
  if (!responseData) return currentFields;

  const { submission, ratings } = responseData;
  const next = { ...currentFields };

  if (submission) {
    if (submission.classes_taught != null) {
      next.classesTaught = submission.classes_taught;
    }
    if (submission.integrated_before != null) {
      next.integratedBefore = submission.integrated_before;
    }
    if (submission.challenges_faced != null) {
      next.challengesFaced = submission.challenges_faced;
    }
    if (submission.subjects_taught != null) {
      next.subjectsTaught = submission.subjects_taught;
    }
    if (submission.qualification != null) {
      next.qualification = submission.qualification;
    }
    if (submission.teaching_experience != null) {
      next.teachingExperience = submission.teaching_experience;
    }

  }

  if (Array.isArray(ratings)) {
    ratings.forEach((r) => {
      const fieldKey = fromApiQuestionCode(r.question_code);
      if (fieldKey && r.rating_value !== undefined && r.rating_value !== null) {
        next[fieldKey] = String(r.rating_value);
      }
    });
  }

  return next;
}

export default function PreTrainingFeedback() {
  const [fields, setFields] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Existing-feedback lookup (GET /get-pre-training-form). This becomes
  // populated once a submission already exists; if nothing exists yet,
  // the form just stays in its normal empty/default state.
  const [isLoadingExisting, setIsLoadingExisting] = useState(true);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);
  const [existingLoadError, setExistingLoadError] = useState("");

  const fetchExistingFeedback = async () => {
    setIsLoadingExisting(true);
    setExistingLoadError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${base_url}${GET_ENDPOINT}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const responseData = res?.data?.data;

      if (res?.data?.success && responseData) {
        setFields((prev) => mapExistingFeedbackToFields(responseData, prev));
        setHasExistingFeedback(true);
      } else {
        setHasExistingFeedback(false);
      }
    } catch (err) {
      // 404 / no record yet is expected for a brand-new form — treat
      // it the same as "no data", not as an error banner.
      if (err?.response?.status !== 404) {
        console.error("Failed to load existing pre-training feedback:", err);
        setExistingLoadError(
          err?.response?.data?.message ||
          err?.message ||
          "Could not load your previous responses.",
        );
      }
      setHasExistingFeedback(false);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Load any existing feedback once on mount.
  useEffect(() => {
    fetchExistingFeedback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (value) => {
    if (key == "classesTaught") {
      localStorage.setItem("grades", value);
    }
    setFields((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    const errs = validate(fields);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(firstKey);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitError("");
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const payload = buildFeedbackPayload(fields);

      await axios.post(`${base_url}${POST_ENDPOINT}`, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      await fetchExistingFeedback();
      setSubmitted(true);

      // Refresh from the GET endpoint so the latest saved values are
      // reflected if the user comes back to this form again.
    } catch (err) {
      console.error("Pre-training feedback submission failed:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while submitting your feedback. Please try again.";
      setSubmitError(message);
      setSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    await fetchExistingFeedback();
    // setFields(initialState);
    setErrors({});
    setSubmitted(false);
    setSubmitError("");
  };

  if (submitted) {
    return (
      <div className="kst-page">
        <div className="kst-success-card">
          <div className="kst-success-icon">✓</div>
          <h2 className="kst-success-title">Thanks for your feedback!</h2>
          <p className="kst-success-text">
            Your responses have been recorded and will help us shape the
            training sessions.
          </p>
          {/* <div className="kst-success-details">
            <p>
              <strong>Name:</strong> {fields.name}
            </p>
            <p>
              <strong>School:</strong> {fields.schoolName}
            </p>
            <p>
              <strong>Training Dates:</strong> {fields.trainingStart} to{" "}
              {fields.trainingEnd}
            </p>
          </div> */}
          <button className="kst-clear-btn" onClick={handleClear}>
            Thank you for submission
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kst-page">
      <RatingStyles />

      {/* Title Card */}
      <div className="kst-title-card">
        <h1 className="kst-form-title">Pre-Training Questionnaire</h1>
        <p className="kst-form-desc">
          Share your valuable feedback. Your input helps us improve future
          sessions.
        </p>

        {isLoadingExisting && (
          <div className="kst-loading-banner">
            <span>⏳</span>
            <span>Checking for your previous responses...</span>
          </div>
        )}

        {/* {!isLoadingExisting && hasExistingFeedback && (
          <div className="kst-loaded-banner">
            <span>ℹ️</span>
            <span>
              We found your previous responses and pre-filled them below — feel
              free to review and update before submitting again.
            </span>
          </div>
        )} */}

        {!isLoadingExisting && existingLoadError && (
          <div className="kst-loaded-banner">
            <span>⚠️</span>
            <span>{existingLoadError}</span>
          </div>
        )}
      </div>

      {/* <p className="kst-required-note">* Indicates required question</p> */}

      <div className="kvs-card-grid">
        {/* Name */}
        {/* <div className="kvs-card">
          <FormInput
            id="name"
            type="text"
            label="Name"
            required
            placeholder="Your answer"
            value={fields.name}
            onChange={set("name")}
            error={errors.name}
          />
        </div> */}

        {/* School Name */}
        {/* <div className="kvs-card">
          <FormInput
            id="schoolName"
            type="text"
            label="School Name"
            required
            placeholder="Your answer"
            value={fields.schoolName}
            onChange={set("schoolName")}
            error={errors.schoolName}
          />
        </div> */}

        {/* KV-Code */}
        {/* <div className="kvs-card">
          <FormInput
            id="kvCode"
            type="text"
            label="KV-Code"
            placeholder="Your answer"
            value={fields.kvCode}
            onChange={set("kvCode")}
          />
        </div> */}

        {/* Date of Training: Start */}
        {/* <div className="kvs-card">
          <FormInput
            id="trainingStart"
            type="date"
            label="Date of Training: Start"
            required
            value={fields.trainingStart}
            onChange={set("trainingStart")}
            error={errors.trainingStart}
          />
        </div> */}

        {/* Date of Training: End */}
        {/* <div className="kvs-card">
          <FormInput
            id="trainingEnd"
            type="date"
            label="Date of Training: End"
            required
            value={fields.trainingEnd}
            onChange={set("trainingEnd")}
            error={errors.trainingEnd}
          />
        </div> */}

        {/* Teaching experience */}

        {/* Subjects taught */}
        <div className="kvs-card">
          <FormInput
            id="subjectsTaught"
            type="text"
            label="Which subject(s) do you currently teach?"
            required
            placeholder="Your answer"
            value={fields.subjectsTaught}
            onChange={set("subjectsTaught")}
            error={errors.subjectsTaught}
          />
        </div>

        {/* Classes taught */}
        <div className="kvs-card">
          <FormInput
            id="classesTaught"
            type="select"
            label="Which classes/grades did you teach?"
            required
            options={[
              "Grades 6 to 9",
              "Grades 11 to 12",
              "Diploma",
              "Graduate",
              "Post Graduate",
              "PhD",
            ]}
            value={fields.classesTaught}
            onChange={set("classesTaught")}
            error={errors.classesTaught}
          />
        </div>

        {/* Integrated before */}
        <div className="kvs-card">
          <FormInput
            id="teachingExperience"
            type="select"
            label="How many years of teaching experience do you have?"
            required
            placeholder="Choose"
            options={experienceOptions}
            value={fields.teachingExperience}
            onChange={set("teachingExperience")}
            error={errors.teachingExperience}
          />
        </div>
        <div className="kvs-card">
          <FormInput
            id="integratedBefore"
            type="select"
            label="Have you integrated Green Technologies/Robotics & IOT  concepts into your teaching curriculum before?"
            required
            options={["Yes", "No"]}
            value={fields.integratedBefore}
            onChange={set("integratedBefore")}
            error={errors.integratedBefore}
          />
        </div>

        {/* Qualification */}
        <div className="kvs-card  ">
          <FormInput
            id="qualification"
            type="select"
            label="What is your highest qualification and specialization/field of study?"
            required
            options={[
              "Graduate (BTech/BSc/BCA/B.Ed.)",
              "Post Graduate (MTech/MSc/MCA/B.Ed.)",
              "PhD",
            ]}
            value={fields.qualification}
            onChange={set("qualification")}
            error={errors.qualification}
          />
        </div>

        {/* Interest — Emerging Tech */}
      </div>
      <div style={{ marginTop: "20px" }}>
        <div className="kvs-card  ">
          <RatingCard
            id="interestEmergingTech"
            min={1}
            max={5}
            label={` How interested are you in learning about the Sustainable
            Technology/Robotics   IOT in your domain?`}
            required
            minLabel="Not at all Interested"
            maxLabel="Very Interested"
            value={fields.interestEmergingTech}
            onChange={set("interestEmergingTech")}
            error={errors.interestEmergingTech}
          />
        </div>

        {/* Interest — Programming Concepts */}
        <div className="kvs-card  ">
          <RatingCard
            id="interestProgramming"
            min={1}
            label={` How interested are you in learning about "Programming Concepts"?`}
            max={5}
            required
            minLabel="Not at all Interested"
            maxLabel="Very Interested"
            value={fields.interestProgramming}
            onChange={set("interestProgramming")}
            error={errors.interestProgramming}
          />
        </div>

        {/* Interest — Designing Classroom Projects */}
        <div className="kvs-card  ">
          <RatingCard
            id="interestProjects"
            min={1}
            max={5}
            label={` How interested are you in learning about "Designing Classroom
            Projects"?`}
            required
            minLabel="Not at all Interested"
            maxLabel="Very Interested"
            value={fields.interestProjects}
            onChange={set("interestProjects")}
            error={errors.interestProjects}
          />
        </div>

        {/* Effectiveness — hands-on */}
        <div className="kvs-card  ">
          <RatingCard
            id="effectivenessHandsOn"
            min={1}
            max={5}
            label={` Rate the effectiveness of Hands-on workshops, Group discussions, and
            Competition-based learning (e.g., hackathons) in enhancing your
            Green Technology/Robotics   IOT skills.`}
            required
            minLabel="Not at all Effective"
            maxLabel="Very Effective"
            value={fields.effectivenessHandsOn}
            onChange={set("effectivenessHandsOn")}
            error={errors.effectivenessHandsOn}
          />
        </div>

        {/* Effectiveness — lectures */}
        <div className="kvs-card  ">
          <RatingCard
            id="effectivenessLectures"
            min={1}
            max={5}
            label={`Rate the effectiveness of Lectures and presentations in enhancing
            your Sustainable Development/Robotics/IOT skills.`}
            required
            minLabel="Not at all Effective"
            maxLabel="Very Effective"
            value={fields.effectivenessLectures}
            onChange={set("effectivenessLectures")}
            error={errors.effectivenessLectures}
          />
        </div>

        {/* Knowledge pre-sessions */}
        <div className="kvs-card  ">
          <RatingCard
            id="knowledgePreSessions"
            min={1}
            label={`How would you rate your overall knowledge of Sustainable
            Development/Robotics/IOT, pre sessions?`}
            max={5}
            required
            minLabel="No Knowledge"
            maxLabel="Extensive Knowledge"
            value={fields.knowledgePreSessions}
            onChange={set("knowledgePreSessions")}
            error={errors.knowledgePreSessions}
          />
        </div>

        {/* Familiarity with concepts */}
        <div className="kvs-card  ">
          <RatingCard
            id="familiarityConcepts"
            min={1}
            max={5}
            label={`Are you familiar with the concepts within the field of Sustainable Development/Robotics/IOT?`}
            required
            minLabel="Not at all familiar"
            maxLabel="Very much familiar"
            value={fields.familiarityConcepts}
            onChange={set("familiarityConcepts")}
            error={errors.familiarityConcepts}
          />
        </div>

        {/* Tech usage frequency */}
        <div className="kvs-card  ">
          <RatingCard
            id="techUsageFrequency"
            min={1}
            max={5}
            label={`How often do you use technology in your classroom?`}
            required
            minLabel="Never heard of it"
            maxLabel="Mostly"
            value={fields.techUsageFrequency}
            onChange={set("techUsageFrequency")}
            error={errors.techUsageFrequency}
          />
        </div>

        {/* Challenges faced */}
        <div className="kvs-card  ">
          <FormInput
            id="challengesFaced"
            type="textarea"
            label="What challenges do you face while using technology in teaching?"
            required
            placeholder="Your answer"
            rows={4}
            value={fields.challengesFaced}
            onChange={set("challengesFaced")}
            error={errors.challengesFaced}
          />
        </div>
      </div>

      {/* Submit */}
      <p className="kst-copy-note">
        A copy of your responses will be emailed to you if you're signed in.
      </p>
      {submitError && <p className="kst-submit-error">{submitError}</p>}
      {/* {submitted && ( */}
      {!hasExistingFeedback && (
        <div className="kst-submit-row">
          <button
            className="kst-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
          <button
            className="kst-clear-btn"
            onClick={handleClear}
            disabled={isSubmitting}
          >
            Clear form
          </button>
        </div>
      )}
      {/* )} */}
      <p className="kst-footer">Never submit passwords through Google Forms.</p>
    </div>
  );
}
